import { CellStyle } from "xlsx-js-style";
import { boldGrayBgLeft, boldLightGrayBgLeft } from "src/app/domain/excel/templates/comparison/mapping";
import { formats, getCell, withCellStyles } from "src/app/domain/excel/utils";
import { Rows } from "src/app/domain/excel/interfaces";
import { Overview, PortfolioPerformanceByCompany } from "src/app/domain/organization/components/org-pp-funds-overview/org-pp-funds-overview.component";

export const getPreparedData = (data: Overview[], headers: string[]): Rows => {
  let newData: Rows = [];
  const headersForDisplay = [];
  headers.forEach((header) => headersForDisplay.push(withCellStyles(header, leftBold)));

  newData.push(headersForDisplay);
  newData = getManyRows(data, headers, newData);
  return newData;
};

export const totalIndexArray = [];

export const getManyRows = (data: Overview[], headers: string[], newData: Rows): Rows => {
  let currentIndex = 2;
  data.forEach((pp) => {

    const active = pp.overview.active.pps.filter((p) => p.selectedPP);
    pp.overview.active.pps = active;
    const sold = pp.overview.sold.pps.filter((p2) => p2.selectedPP);
    pp.overview.sold.pps = sold;
    const fundRow = getNewExternalMapping(pp, currentIndex, headers);
    newData.push(fundRow);
    currentIndex += 1;
    if (active.length) {
      const activeNameRow = getNewActiveSoldMapping(pp, headers, currentIndex, 'Active');
      newData.push(...activeNameRow);
      currentIndex += 1;
      active.forEach((el, index) => newData.push(getInternalMapping(el, currentIndex + index, headers)));
      currentIndex += active.length;
    }
    if (sold.length) {
      const soldNameRow = getNewActiveSoldMapping(pp, headers, currentIndex, 'Realized');
      newData.push(...soldNameRow);
      currentIndex += 1;
      sold.forEach((el, iSold) => newData.push(getInternalMapping(el, currentIndex + iSold, headers)));
      currentIndex += sold.length;
    }

    const subTotal = getNewSubTotalMapping(pp, currentIndex, headers, active.length, sold.length);
    newData.push(subTotal);
    currentIndex += 1;

  });
  const totalMapping = getTotalMapping(currentIndex, headers);
  newData.push(totalMapping);
  return newData;
};

export const getNewActiveSoldMapping = (pp, headers: string[], currentIndex: number, status: string): Rows => {
  const rows: Rows = [];
  const row = [];
  const dataLen = status === 'Realized' ? pp.overview.sold.pps.length : pp.overview.active.pps.length;
  const body = {
    Fund: status,
    Period: '',
    Investment: getCell({
      f: `SUM(C${currentIndex + 1}:C${currentIndex + dataLen})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: `SUM(D${currentIndex + 1}:D${currentIndex + dataLen})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/Loss': getCell({
      f: `(D${currentIndex}-C${currentIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR((D${currentIndex}/C${currentIndex}),0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({
      v: '-',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Holding: '-',
    'Company value': '-',
  };

  for (const header of headers) {
    row.push(withCellStyles(body[header], leftBold));
  }
  rows.push(row);
  return rows;
};

export const getNewExternalMapping = (portfolio: Overview, i: number, headers: string[]): any[] => {
  const rows: Rows = [];
  const row = [];
  const body = {
    Fund: portfolio.fundName + ' (' + String(portfolio.overview.selectedFundPeriod) + ')',
    Period: portfolio.overview.selectedFundPeriod,
    Investment: '',
    'Fair value': '',
    'Gain/Loss': '',
    MOIC: '',
    IRR: '',
    Holding: '',
    'Company value': '',
  };
  for (const header of headers) {
    row.push(withCellStyles(body[header], boldLightGrayBgLeft));
  }
  rows.push(row);
  return row;
};

export const getInternalMapping = (portfolio: PortfolioPerformanceByCompany, i: number, headers: string[]): any[] => {
  const row = [];
  const rows: Rows = [];
  const body = {
    Fund: portfolio.company.name,
    Period: '',
    Investment: getCell({
      v: portfolio.selectedPP.investment,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      v: portfolio.selectedPP.fairValue,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/Loss': getCell({
      f: `(D${i}-C${i})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR((D${i}/C${i}),0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({
      v: dashInsteadOfNull(portfolio.selectedPP.irr),
      z: formats.percents,
      t: 'n',
    }),
    Holding: getCell({
      v: dashInsteadOfNull(portfolio.selectedPP.holding),
      z: formats.percents,
      t: 'n',
    }),
    'Company value': getCell({
      v: dashInsteadOfNull(portfolio.selectedPP.companyValue),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  for (const header of headers) {
    row.push(withCellStyles(body[header], left));
  }
  rows.push(row);
  return row;
};

export const getNewSubTotalMapping = (portfolio: Overview, i: number, headers: string[], activeLen: number, soldLen: number): any[] => {
  let start;
  let stop;
  totalIndexArray.push(i);
  let summInv = '';
  let sumFair = '';
  if (activeLen === 0) {
    start = i - soldLen - 1;
    stop = i - soldLen - 1;
    summInv = `SUM(C${start})`;
    sumFair = `SUM(D${start})`;
  } else if (soldLen === 0) {
    start = i - activeLen - 1;
    stop = i - activeLen - 1;
    summInv = `SUM(C${start})`;
    sumFair = `SUM(D${start})`;
  } else {
    start = i - activeLen - soldLen - 2;
    stop = i - soldLen - 1;
    summInv = `SUM(C${start},C${stop})`;
    sumFair = `SUM(D${start},D${stop})`;
  }
  const rows: Rows = [];
  const row = [];
  const body = {
    Fund: 'Total',
    Period: '',
    Investment: getCell({
      f: summInv,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: sumFair,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/Loss': getCell({
      f: `(D${i}-C${i})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR((D${i}/C${i}),0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({
      v: dashInsteadOfNull(portfolio.overview.total.irr),
      z: formats.percentsNoSpace,
      t: 'n',
    }),
    Holding: '',
    'Company value': '',
  };
  for (const header of headers) {
    row.push(withCellStyles(body[header], boldLightGrayBgLeft));
  }
  rows.push(row);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return row;
};


export const getTotalMapping = (currentIndex: number, headers: string[]): any[] => {
  const row: any[] = [];
  const rows: Rows = [];
  const body = {
    Fund: 'Grand total',
    Period: '',
    Investment: getCell({
      f: getSum('C'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: getSum('D'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/Loss': getCell({
      f: `D${currentIndex}-C${currentIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    MOIC: getCell({
      f: `IFERROR(D${currentIndex}/C${currentIndex},0)`,
      z: formats.multiplier,
      t: 'n',
    }),
    IRR: getCell({
      v: '',
      z: formats.percents,
      t: 'n',
    }),
    Holding: getCell({
      v: '-',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Company value': getCell({
      v: '-',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  for (const header of headers) {
    row.push(withCellStyles(body[header], boldGrayBgLeft));
  }
  rows.push(row);
  return row;
};

export const getSum = (letter: string): string => {

  const result = totalIndexArray.map((totalI) => `${letter}${totalI}`).join(',');
  return `SUM(${result})`;

};

// eslint-disable-next-line
export const dashInsteadOfNull = (value: any) => ((value || value === 0) ? value : '-');

const left: CellStyle = {
  alignment: { horizontal: 'left' },
};

const leftBold: CellStyle = {
  alignment: { horizontal: 'left' },
  font: { bold: true },
};
